import React, { useEffect, useState } from 'react';
import { TESTIMONIALS } from '../mock';
import { api } from '../api';
import { Star, Quote } from 'lucide-react';

const gradientPalette = [
  'from-blue-500 to-purple-500',
  'from-pink-500 to-rose-500',
  'from-amber-500 to-orange-500',
  'from-emerald-500 to-teal-500',
  'from-sky-500 to-indigo-500',
  'from-fuchsia-500 to-pink-500',
];

// Simulated review distribution
const RATING_BREAKDOWN = [
  { stars: 5, pct: 89 },
  { stars: 4, pct: 8 },
  { stars: 3, pct: 2 },
  { stars: 2, pct: 0.6 },
  { stars: 1, pct: 0.4 },
];

const TestimonialCard = ({ t, i, brandName }) => {
  const quote =
    typeof t.quote === 'string'
      ? t.quote.replace(/\{\{brand\}\}/g, brandName)
      : t.quote;

  return (
    <div className="shrink-0 w-[320px] md:w-[380px] p-6 rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all">
      <Quote className="absolute top-5 right-5 w-8 h-8 text-slate-100" />

      <div className="flex items-center gap-3">
        <div
          className={`w-11 h-11 rounded-full bg-gradient-to-br ${
            gradientPalette[i % gradientPalette.length]
          } text-white flex items-center justify-center font-bold text-sm`}
        >
          {t.avatar}
        </div>

        <div className="min-w-0">
          <div className="font-extrabold text-[#0a1930] truncate">
            {t.name}
          </div>

          <div className="text-xs text-slate-500 truncate">
            {t.role}
          </div>
        </div>
      </div>

      <div className="mt-3 flex gap-0.5">
        {[...Array(5)].map((_, k) => (
          <Star
            key={k}
            className="w-4 h-4 fill-amber-400 text-amber-400"
          />
        ))}
      </div>

      <p className="mt-3 text-slate-700 leading-relaxed text-sm line-clamp-6">
        "{quote}"
      </p>
    </div>
  );
};

const RatingSummary = () => (
  <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-6 items-center mb-12">
    <div className="text-center md:text-right md:border-r md:pr-6 border-slate-200">
      <div className="text-6xl font-extrabold plixy-gradient-text leading-none">
        4.91
      </div>

      <div className="mt-2 flex justify-center md:justify-end gap-0.5">
        {[...Array(5)].map((_, k) => (
          <Star
            key={k}
            className="w-4 h-4 fill-amber-400 text-amber-400"
          />
        ))}
      </div>

      <div className="mt-1 text-xs font-bold text-slate-500 uppercase tracking-widest">
        out of 5
      </div>
    </div>

    <div className="md:col-span-2 space-y-1.5">
      {RATING_BREAKDOWN.map((r) => (
        <div
          key={r.stars}
          className="flex items-center gap-3 text-sm"
        >
          <span className="w-10 shrink-0 font-bold text-[#0a1930] inline-flex items-center gap-0.5">
            {r.stars}{' '}
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
          </span>

          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-1000"
              style={{ width: `${r.pct}%` }}
            />
          </div>

          <span className="w-14 shrink-0 text-right text-xs font-bold text-slate-500 tabular-nums">
            {r.pct}%
          </span>
        </div>
      ))}

      <div className="pt-2 text-xs text-slate-500">
        Based on{' '}
        <span className="font-bold text-[#0a1930]">
          15,247
        </span>{' '}
        verified reviews across G2, Trustpilot & Capterra.
      </div>
    </div>
  </div>
);

const Reviews = () => {
  const [brandName, setBrandName] = useState('IGGrowth');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await api.getPublicSettings();

        setBrandName(
          settings?.brand_name ||
          settings?.brandName ||
          'IGGrowth'
        );
      } catch (error) {
        // Keep the safe fallback if public settings are unavailable.
      }
    };

    loadSettings();
  }, []);

  // Two rows, alternating directions
  const doubledA = [...TESTIMONIALS, ...TESTIMONIALS];

  const doubledB = [
    ...TESTIMONIALS.slice(3),
    ...TESTIMONIALS.slice(0, 3),
    ...TESTIMONIALS.slice(3),
    ...TESTIMONIALS.slice(0, 3),
  ];

  return (
    <section
      id="resources"
      className="py-24 bg-slate-50/50 overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-sm font-bold tracking-widest uppercase plixy-gradient-text">
            Reviews
          </p>

          <h2 className="mt-3 text-4xl md:text-5xl font-extrabold text-[#0a1930]">
            Trusted by the top marketers.
            <br />
            Loved by every user.
          </h2>
        </div>

        <RatingSummary />
      </div>

      {/* Carousel row 1 — scroll left */}
      <div className="reviews-marquee-wrap mb-6">
        <div className="reviews-marquee-left">
          {doubledA.map((t, i) => (
            <div
              key={`a-${i}`}
              className="relative"
            >
              <TestimonialCard
                t={t}
                i={i}
                brandName={brandName}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Carousel row 2 — scroll right */}
      <div className="reviews-marquee-wrap">
        <div className="reviews-marquee-right">
          {doubledB.map((t, i) => (
            <div
              key={`b-${i}`}
              className="relative"
            >
              <TestimonialCard
                t={t}
                i={i + 3}
                brandName={brandName}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
